import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, ShieldCheck, CreditCard } from 'lucide-react';
import axios from 'axios';

/**
 * NedarimCardModal — PostNedarim card-iframe donation flow (the "iframe" page
 * from docs/nedarim-iframe-example.html). OUR form collects everything (name,
 * phone, email, ת"ז, amount, custom fields); Nedarim's embedded iframe collects
 * only the card number + expiry + CVV (its own ת"ז input is hidden and ignored
 * in FinishTransaction2). On "pay" we first stash the campaign + custom-field answers in
 * a PaymentSession (POST /public/nedarim/session) and then postMessage
 * `FinishTransaction2` with the FULL Nedarim param list — including the session
 * token in Param1, so the org's webhook can copy the custom fields onto the
 * resulting Donation. The iframe posts back `Height` (resize) and
 * `TransactionResponse` (result).
 *
 * Why a card-iframe rather than Nedarim's hosted online page: only an
 * on-our-form flow lets us control Groupe ("<campaign> | <receipt text>"),
 * Comment ("מערכת Amutech"), and carry the campaign's custom fields.
 */

const API_BASE = '/api';
const NEDARIM_IFRAME_SRC = 'https://matara.pro/nedarimplus/iframe?language=he';

interface NedarimCardModalProps {
  open: boolean;
  onClose: () => void;
  mosadId: string;
  apiValid: string;
  campaignId: string;
  campaignName: string;
  receiptMessage?: string;
  amount: number;
  currency: 'ILS' | 'USD';
  isRecurring: boolean;
  recurringMonths?: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  idNumber?: string;
  street?: string;
  city?: string;
  ambassadorSlug?: string | null;
  customFields?: Record<string, any>;
  onSuccess: (payload: Record<string, string>) => void;
  onFail: (payload: Record<string, string>) => void;
}

export default function NedarimCardModal({
  open, onClose, mosadId, apiValid, campaignId, campaignName, receiptMessage,
  amount, currency, isRecurring, recurringMonths, firstName, lastName, phone, email,
  idNumber, street, city, ambassadorSlug, customFields, onSuccess, onFail,
}: NedarimCardModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(420);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Latest callbacks/values behind a ref, so the effects below can depend on
  // `open` ALONE. The parent recreates onClose/onSuccess/onFail on every
  // render, and the public campaign page re-renders every few seconds (the
  // donation poll) — with them in the deps, each poll tick re-ran the reset
  // effect, flipping `loading` back on and burying the already-loaded iframe
  // under the "טוען טופס" overlay before the donor could type a card.
  const latest = useRef({ amount, currency, onClose, onSuccess, onFail });
  latest.current = { amount, currency, onClose, onSuccess, onFail };

  // Reset transient UI state each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setLoading(true);
    setIframeHeight(420);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') latest.current.onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Messages from Nedarim's iframe: Height (recommended height) +
  // TransactionResponse (the charge result). Origin-guarded to matara.pro.
  useEffect(() => {
    if (!open) return;
    const onMessage = (ev: MessageEvent) => {
      let host = '';
      try { host = new URL(ev.origin).hostname; } catch { return; }
      if (!/(^|\.)matara\.pro$/.test(host)) return;
      const data: any = ev.data;
      if (!data || !data.Name) return;

      if (data.Name === 'Height') {
        const h = Number(data.Value);
        if (Number.isFinite(h) && h > 0) setIframeHeight(h + 20);
      } else if (data.Name === 'TransactionResponse') {
        setSubmitting(false);
        const val = data.Value || {};
        const status = String(val.Status || '');
        if (status === 'OK' || status === 'Success') {
          latest.current.onSuccess({
            provider: 'nedarim',
            transactionId: String(val.TransactionId || ''),
            amount: String(latest.current.amount),
            currency: latest.current.currency,
          });
        } else {
          latest.current.onFail({ error: String(val.Message || 'העסקה נכשלה') });
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open]);

  const pay = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // 1. Stash campaign + custom fields in a PaymentSession; get the token +
      //    the org's CallBack URL (kept off the public campaign payload).
      const { data } = await axios.post(`${API_BASE}/public/nedarim/session`, {
        campaignId,
        ambassadorSlug: ambassadorSlug || '',
        amount,
        currency,
        donorName: [firstName, lastName].filter(Boolean).join(' ').trim(),
        donorEmail: email,
        donorPhone: phone,
        isRecurring,
        recurringMonths,
        customFields: customFields && Object.keys(customFields).length ? customFields : undefined,
      });
      const token = String(data?.token || '');
      const callbackUrl = String(data?.callbackUrl || '');
      if (!token || !callbackUrl) {
        setSubmitting(false);
        onFail({ error: 'שגיאה בהכנת התשלום' });
        return;
      }

      // 2. Build the FULL FinishTransaction2 param list (all params, even empty).
      // Groupe prints on Nedarim's receipt: "<campaign> | <receipt text> | Amutech".
      // The campaign name MUST stay the first ' | ' segment — the webhook and the
      // income page both attribute by split(' | ')[0]. Nedarim caps Groupe at 100
      // chars, so the middle (receipt text) segment shrinks first if needed.
      const BRAND = 'Amutech';
      const GROUPE_MAX = 100;
      let receiptSeg = (receiptMessage || '').trim();
      const overhead = campaignName.length + ` | ${BRAND}`.length + (receiptSeg ? 3 : 0);
      if (receiptSeg && overhead + receiptSeg.length > GROUPE_MAX) {
        receiptSeg = receiptSeg.slice(0, Math.max(0, GROUPE_MAX - overhead)).trim();
      }
      const groupe = [campaignName, receiptSeg, BRAND].filter(Boolean).join(' | ').slice(0, GROUPE_MAX);
      const isHK = isRecurring;
      const value: Record<string, string> = {
        Mosad: mosadId,
        ApiValid: apiValid,
        PaymentType: isHK ? 'HK' : 'Ragil',
        Currency: currency === 'USD' ? '2' : '1',

        // ת"ז MUST be sent from here: in FinishTransaction2 the iframe ignores
        // its own (hidden-by-default) Zeout input and forwards only this value.
        // Without it Nedarim records the donation with no donor ID.
        Zeout: idNumber || '',
        FirstName: firstName || '',
        LastName: lastName || '',
        Street: street || '',
        City: city || '',
        Phone: phone || '',
        Mail: email || '',

        Amount: String(amount),
        // Ragil → #installments (1). HK → #months ('' = unlimited).
        Tashlumim: isHK ? (recurringMonths && recurringMonths > 0 ? String(recurringMonths) : '') : '1',
        Day: '', // HK charge-day — Nedarim defaults it
        Groupe: groupe,
        Comment: 'מערכת Amutech',

        Param1: `session:${token}`,
        Param2: ambassadorSlug ? `ambassador:${ambassadorSlug}` : '',

        ForceUpdateMatching: '1',
        CallBack: callbackUrl,
        CallBackMailError: '',
        // The iframe forwards these keys unconditionally; a missing key becomes
        // the literal string "undefined" on Nedarim's server. ThirdPartyReceipt
        // especially must stay '' — any truthy value marks the transaction as
        // "receipt already issued elsewhere" and Nedarim never issues a קבלה.
        ThirdPartyReceipt: '',
        Tokef: '', // expiry is typed inside Nedarim's iframe
      };

      iframeRef.current?.contentWindow?.postMessage({ Name: 'FinishTransaction2', Value: value }, '*');
      // The iframe replies with TransactionResponse (handled above); keep the
      // button disabled until then.
    } catch (err: any) {
      setSubmitting(false);
      onFail({ error: 'שגיאה בהכנת התשלום' });
    }
  };

  if (!open) return null;

  return (
    <div
      dir="rtl"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 md:p-6"
      style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: '94vh' }}>
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-gray-200 bg-gradient-to-l from-blue-50 to-white shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">תרומה דרך נדרים פלוס</h2>
          </div>
          <button onClick={onClose} aria-label="סגור" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <Loader2 size={32} className="animate-spin text-blue-500" />
                  <span className="text-sm font-medium">טוען טופס תשלום מאובטח...</span>
                </div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={NEDARIM_IFRAME_SRC}
              title="תשלום מאובטח — נדרים פלוס"
              onLoad={() => setLoading(false)}
              style={{ width: '100%', height: iframeHeight, border: 0, borderRadius: 10 }}
              allow="payment *"
            />
          </div>

          <button
            onClick={pay}
            disabled={submitting}
            className="w-full mt-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-base font-bold flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? <Loader2 size={20} className="animate-spin" /> : <CreditCard size={20} />}
            {submitting ? 'מבצע חיוב...' : 'בצע חיוב'}
          </button>
        </div>
      </div>
    </div>
  );
}
